import { Config } from '@/config';
import { ApiService } from '@/services/api.service';
import { transformActionFilters } from '@/util/action-filters-util';
import {
  formatDate,
  formatStandardDate,
  getDaysBeforeClosing,
  isUnknownDate,
  SHORT_DATE_TIME_FORMAT,
  toMoment,
} from '@/util/date-util';
import { expandToZones } from '@/util/zones-util';
import axios from 'axios';

const CancelToken = axios.CancelToken;
let cancelTokenSearch = null;
let cancelTokenDetail = null;
let cancelTokenTenderMatch = null;
let cancelTokenTendersMatch = null;
let cancelTokenTenderRate = null;

class TendersService {
  getExportTenders({ mode, parameters }) {
    const body = buildTenderSearchBody({ mode, parameters });
    const isDetailed = parameters.hasOwnProperty('tenderIds');

    return ApiService.post(`${Config.api.tender.url}tenders/mode/${mode}/export?detailed=${isDetailed}`, body, {
      responseType: 'arraybuffer',
    });
  }

  // `cancelable: false` opts out of the shared search cancel token — the request neither
  // cancels an in-flight search nor gets cancelled by the next one.
  getTendersByMode({ parameters, mode, page, sortingField, sortingOrder, cancelable = true }) {
    if (cancelable) {
      if (cancelTokenSearch) cancelTokenSearch.cancel('Multiple requests.');
      cancelTokenSearch = CancelToken.source();
    }

    const body = buildTenderSearchBody({ mode, parameters });

    return ApiService.post(
      `${Config.api.tender.url}tenders/mode/${mode}?page=${page}&sortingField=${sortingField}&sortingOrder=${sortingOrder}`,
      body,
      cancelable ? { cancelToken: cancelTokenSearch.token } : undefined,
    ).then((response) => {
      const tenders = mapSearchResults(response.data.items);
      const pagination = {
        page: response.data.page,
        totalCount: response.data.totalCount,
        totalPages: response.data.totalPages,
        nextPage: response.data.nextPage,
        previousPage: response.data.previousPage,
      };

      return { tenders, pagination };
    });
  }

  // New views-based search. Send `viewId` to run a saved view, or `filters` (the raw
  // view-filter blob) for the live/tweaked working state. If both are given, `filters` wins.
  searchTenders({ viewId, filters, page, pageSize, sortingField, sortingOrder }) {
    if (cancelTokenSearch) cancelTokenSearch.cancel('Multiple requests.');
    cancelTokenSearch = CancelToken.source();

    const body = filters ? { filters } : { viewId };
    const query = `page=${page}&pageSize=${pageSize}&sortingField=${sortingField}&sortingOrder=${sortingOrder}`;

    return ApiService.post(`${Config.api.tender.url}tenders/search?${query}`, body, {
      cancelToken: cancelTokenSearch.token,
    }).then((response) => {
      const tenders = mapSearchResults(response.data.items);
      const pagination = {
        page: response.data.page,
        totalCount: response.data.totalCount,
        totalPages: response.data.totalPages,
        nextPage: response.data.nextPage,
        previousPage: response.data.previousPage,
      };

      return { tenders, pagination };
    });
  }

  getMatchesCount() {
    return ApiService.post(`${Config.api.tender.url}tenders/count`, {}).then((response) => response.data);
  }

  getExploreCount() {
    return ApiService.post(`${Config.api.tender.url}tenders/count-keywords`, {}).then((response) => response.data);
  }

  getTenderInfos(tenderId) {
    if (cancelTokenDetail) cancelTokenDetail.cancel('Multiple requests.');
    cancelTokenDetail = CancelToken.source();

    return ApiService.get(`${Config.api.tender.url}tenders/${tenderId}?includeMatchInformation=false`, {
      cancelToken: cancelTokenDetail.token,
    }).then((response) => {
      let tender = response.data;

      const endDate =
        tender.tenderPeriod && !isUnknownDate(tender.tenderPeriod.endDate) ? tender.tenderPeriod.endDate : null;

      tender.published = !isUnknownDate(tender.publishedDate) ? formatStandardDate(tender.publishedDate) : '-';
      tender.score = tender.tenantTender.relevanceScore;
      tender.daysBeforeClosing = getDaysBeforeClosing(endDate);
      tender.closingDate = endDate ? formatStandardDate(endDate) : '-';
      tender.completeClosingDate = endDate ? formatDate(toMoment(endDate), SHORT_DATE_TIME_FORMAT) : null;
      tender.closingDateDetails = endDate ? formatDate(toMoment(endDate), 'ddd ll HH:mm z') : null;
      tender.emitter = tender.procuringEntity;
      tender.tenantTender = tender.tenantTender || {};
      tender.userTender = tender.userTender || {};
      tender.assignees = tender.assignees || [];
      tender.opportunities = [];
      tender.value = tender.value || null;
      tender.minValue = tender.minValue || null;

      return tender;
    });
  }

  getTenderMatches({ id, keywords }) {
    if (cancelTokenTenderMatch) cancelTokenTenderMatch.cancel('Multiple requests.');
    cancelTokenTenderMatch = CancelToken.source();
    return postTenderMatch({ id, keywords, cancelToken: cancelTokenTenderMatch.token });
  }

  // Same split as the Explore table: empty keywords → tenant coverage (matchedKeywords);
  // search-bar keywords → title/description highlights only.
  getTenderMatchesForExplore({ id, searchKeywords }) {
    if (cancelTokenTenderMatch) cancelTokenTenderMatch.cancel('Multiple requests.');
    cancelTokenTenderMatch = CancelToken.source();
    const cancelToken = cancelTokenTenderMatch.token;

    const tenantCoverage = postTenderMatch({ id, keywords: [], cancelToken });

    const hasSearchKeywords = Array.isArray(searchKeywords) && searchKeywords.length > 0;
    if (!hasSearchKeywords) return tenantCoverage;

    const searchHighlights = postTenderMatch({ id, keywords: searchKeywords, cancelToken });

    return Promise.all([tenantCoverage, searchHighlights]).then(([tenantMatches, highlightMatches]) => ({
      matchedKeywords: (tenantMatches && tenantMatches.matchedKeywords) || [],
      highlights:
        (highlightMatches && highlightMatches.highlights) || (tenantMatches && tenantMatches.highlights) || {},
    }));
  }

  getTendersMatches({ tenderIds, keywords, silent = false }) {
    return postTendersMatches({
      tenderIds,
      keywords,
      cancelToken: tokenForTendersMatch({ silent }),
    });
  }

  // Explore: two match-information calls sharing one cancel token —
  // empty keywords → tenant coverage (matchedKeywords); search keywords → title/description highlights.
  getTendersMatchesForExplore({ tenderIds, searchKeywords, silent = false }) {
    const cancelToken = tokenForTendersMatch({ silent });

    const tenantCoverage = postTendersMatches({ tenderIds, keywords: [], cancelToken });

    const hasSearchKeywords = Array.isArray(searchKeywords) && searchKeywords.length > 0;
    if (!hasSearchKeywords) return tenantCoverage;

    const searchHighlights = postTendersMatches({ tenderIds, keywords: searchKeywords, cancelToken });

    return Promise.all([tenantCoverage, searchHighlights]).then(([tenantMatches, highlightMatches]) =>
      mergeExploreMatches(tenantMatches, highlightMatches),
    );
  }

  read(tenderId) {
    return ApiService.put(`${Config.api.tender.url}tenders/${tenderId}/read`).then((response) => response.data);
  }

  readMany(tenderIds) {
    return ApiService.put(`${Config.api.tender.url}tenders/read`, tenderIds).then((response) => response.data);
  }

  unread(tenderId) {
    return ApiService.put(`${Config.api.tender.url}tenders/${tenderId}/unread`).then((response) => response.data);
  }

  unreadMany(tenderIds) {
    return ApiService.put(`${Config.api.tender.url}tenders/unread`, tenderIds).then((response) => response.data);
  }

  follow(tenderId) {
    return ApiService.put(`${Config.api.tender.url}tenders/${tenderId}/follow`).then((response) => response.data);
  }

  followMany(tenderIds) {
    return ApiService.put(`${Config.api.tender.url}tenders/follow`, tenderIds).then((response) => response.data);
  }

  unfollow(tenderId) {
    return ApiService.put(`${Config.api.tender.url}tenders/${tenderId}/unfollow`).then((response) => response.data);
  }

  unfollowMany(tenderIds) {
    return ApiService.put(`${Config.api.tender.url}tenders/unfollow`, tenderIds).then((response) => response.data);
  }

  share(tenderId) {
    return ApiService.put(`${Config.api.tender.url}tenders/${tenderId}/share`).then((response) => response.data);
  }

  shareMany(tenderIds) {
    return ApiService.put(`${Config.api.tender.url}tenders/share`, tenderIds).then((response) => response.data);
  }

  unshare(tenderId) {
    return ApiService.put(`${Config.api.tender.url}tenders/${tenderId}/unshare`).then((response) => response.data);
  }

  unshareMany(tenderIds) {
    return ApiService.put(`${Config.api.tender.url}tenders/unshare`, tenderIds).then((response) => response.data);
  }

  archive(tenderId) {
    return ApiService.put(`${Config.api.tender.url}tenders/${tenderId}/archive`).then((response) => response.data);
  }

  archiveMany(tenderIds) {
    return ApiService.put(`${Config.api.tender.url}tenders/archive`, tenderIds).then((response) => response.data);
  }

  unarchive(tenderId) {
    return ApiService.put(`${Config.api.tender.url}tenders/${tenderId}/unarchive`).then((response) => response.data);
  }

  unarchiveMany(tenderIds) {
    return ApiService.put(`${Config.api.tender.url}tenders/unarchive`, tenderIds).then((response) => response.data);
  }

  assign({ tenderId, userIds }) {
    return ApiService.put(`${Config.api.tender.url}tenders/${tenderId}/assignees`, userIds).then(
      (response) => response.data,
    );
  }

  rate({ tenderId, thumbsRating }) {
    if (cancelTokenTenderRate) cancelTokenTenderRate.cancel('Multiple requests.');
    cancelTokenTenderRate = CancelToken.source();

    return ApiService.put(`${Config.api.tender.url}tenders/${tenderId}/thumbs-rating`, thumbsRating, {
      headers: { 'Content-Type': 'application/json' },
      cancelToken: cancelTokenTenderRate.token,
    }).then((response) => response.data);
  }

  unRate({ tenderId }) {
    return this.rate({ tenderId, thumbsRating: null });
  }

  rateMany({ tenderIds, rating }) {
    return ApiService.put(`${Config.api.tender.url}tenders/thumbs-rating`, { tenderIds, rating }).then(
      (response) => response.data,
    );
  }
}

const SEARCH_BODY_STRATEGY = {
  explore: 'FULL',
  received: 'FULL',
  archived: 'QUERY_ONLY',
  shared: 'QUERY_ONLY',
  followed: 'QUERY_ONLY',
};

const buildTenderSearchBody = ({ mode, parameters }) => {
  const strategy = SEARCH_BODY_STRATEGY[mode];
  if (!strategy) return null;

  const body = {};

  const tenderIds = parameters && parameters.tenderIds ? parameters.tenderIds.filter(Boolean) : [];
  if (tenderIds.length > 0) body.tenderIds = tenderIds;

  if (strategy === 'FULL') {
    const mapped = mapToParameters(parameters) || {};
    return { ...mapped, ...body };
  }

  // QUERY_ONLY
  const query = parameters && parameters.query ? parameters.query.trim() : null;
  if (query) body.query = query;

  return body;
};

const mapToParameters = ({
  keywords,
  platforms,
  administrativeRegions,
  classifications,
  statuses,
  closingBefore,
  publishedAfter,
  publishedBefore,
  keywordIds,
  matchContexts,
  procurementCategories,
  zones,
  readFilters,
  shareFilters,
  followFilters,
  assigneesFilters,
  relevanceScoreRange,
  tenderIds,
  query,
  includeUnlocated,
}) => ({
  keywords: keywords ? keywords : undefined,
  platforms: platforms && platforms.length > 0 ? platforms : undefined,
  administrativeRegions: administrativeRegions ? administrativeRegions : undefined,
  classifications: classifications ? classifications : undefined,
  statuses: statuses && statuses.length > 0 ? statuses : undefined,
  closingBefore: closingBefore ? formatStandardDate(closingBefore) : undefined,
  publishedAfter: publishedAfter ? publishedAfter : undefined,
  publishedBefore: publishedBefore ? publishedBefore : undefined,
  keywordIds: keywordIds && keywordIds.length > 0 ? keywordIds : undefined,
  matchContexts: matchContexts && matchContexts.length > 0 ? matchContexts : undefined,
  procurementCategories: procurementCategories && procurementCategories.length > 0 ? procurementCategories : undefined,
  zones: zones ? expandToZones(zones) : undefined,
  read: transformActionFilters(readFilters).read,
  shared: transformActionFilters(shareFilters).shared,
  followed: transformActionFilters(followFilters).followed,
  assignees: assigneesFilters && assigneesFilters.length > 0 ? assigneesFilters : undefined,
  tenderIds: tenderIds && tenderIds.length > 0 ? tenderIds : undefined,
  relevanceScoreRange: relevanceScoreRange && relevanceScoreRange.length > 0 ? relevanceScoreRange : undefined, // relevanceScoreRange is a list of pairs corresponding to start and end ranges
  query: query ? query : undefined,
  includeUnlocated: includeUnlocated ? true : undefined,
});

const mapSearchResults = (results = []) =>
  results.map((r) => ({
    id: r.id,
    title: r.title,
    source: r.source,
    status: r.status,
    emitter: r.procuringEntity,
    score: r.tenantTender.relevanceScore,
    documents: r.documents,
    userTender: r.userTender || {},
    tenantTender: r.tenantTender || {},
    published: r.publishedDate ? toMoment(r.publishedDate) : '-',
    publishedSortable: r.publishedDate ? formatDate(toMoment(r.publishedDate), 'YYYYMMDD') : '-',
    closingDate: r.tenderPeriod && r.tenderPeriod.endDate ? toMoment(r.tenderPeriod.endDate) : '-',
    closingDateSortable:
      r.tenderPeriod && r.tenderPeriod.endDate ? formatDate(toMoment(r.tenderPeriod.endDate), 'YYYYMMDD') : '-',
    daysBeforeClosing: r.tenderPeriod && r.tenderPeriod.endDate ? getDaysBeforeClosing(r.tenderPeriod.endDate) : null,
    assignees: r.assignees ? r.assignees : [],
    opportunities: [],
  }));

// Prefetch (details next/prev page) must not cancel the list coverage request.
const tokenForTendersMatch = ({ silent = false } = {}) => {
  if (silent) {
    if (tokenForTendersMatch._silentSource) tokenForTendersMatch._silentSource.cancel('Multiple requests.');
    tokenForTendersMatch._silentSource = CancelToken.source();
    return tokenForTendersMatch._silentSource.token;
  }
  if (cancelTokenTendersMatch) cancelTokenTendersMatch.cancel('Multiple requests.');
  cancelTokenTendersMatch = CancelToken.source();
  return cancelTokenTendersMatch.token;
};

const postTenderMatch = ({ id, keywords, cancelToken }) => {
  const body = keywords ? keywords : undefined;
  return ApiService.post(`${Config.api.tender.url}tenders/${id}/match-information`, body, { cancelToken }).then(
    (response) => response.data,
  );
};

const postTendersMatches = ({ tenderIds, keywords, cancelToken }) =>
  ApiService.post(`${Config.api.tender.url}tenders/match-information`, { tenderIds, keywords }, { cancelToken }).then(
    (response) => response.data,
  );

// Keep tenant keyword coverage for the column; apply search-bar highlights on title/description.
const mergeExploreMatches = (tenantMatches = {}, highlightMatches = {}) => {
  const merged = { ...tenantMatches };

  Object.entries(highlightMatches).forEach(([tenderId, match]) => {
    const base = merged[tenderId] || { matchedKeywords: [], highlights: {} };
    merged[tenderId] = {
      matchedKeywords: base.matchedKeywords,
      highlights: match.highlights || base.highlights,
    };
  });

  return merged;
};

export const tendersService = new TendersService();
